import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, InputNumber, Select, Table, Typography } from 'antd';
import { debounce } from 'lodash';
// Api
import { GET_FEATURE_LIMITS_GLOBAL } from 'api/featureLimits/queries';
import { SET_GLOBAL_FEATURE_LIMIT } from 'api/featureLimits/mutations';
import { GET_STORES_BY_NAME_AND_EMAIL } from 'api/store/queries';
// Types
import {
  GetStoresByNameAndEmail,
  GetStoresByNameAndEmailVariables,
} from 'api/store/types/GetStoresByNameAndEmail';
import { FeatureLimitKey, UserRole } from 'api/graphql-global-types';
// Helpers
import {
  FEATURE_LIMIT_KEYS,
  FEATURE_LIMIT_LABELS,
  GlobalFeatureLimit,
  PER_USER_ONLY_FEATURE_LIMIT_KEYS,
  formatLimit,
} from './constants';
// Ui
import { successNotification, errorNotification } from 'ui/Notification';
// Components
import UserFeatureLimitsDrawer from './components/UserFeatureLimitsDrawer/UserFeatureLimitsDrawer';
// Styles
import styles from './FeatureLimits.module.scss';

const { Text, Title } = Typography;
const { Option, OptGroup } = Select;

const FEATURE_LIMIT_STORE_ROLES = [
  UserRole.Athlete,
  UserRole.ContentCreator,
  UserRole.Organization,
];

type GlobalRow = {
  featureKey: FeatureLimitKey;
  monthlyLimit: number | null;
};

type SelectedUser = {
  userId: string;
  name: string;
} | null;

const FeatureLimits: React.FC = () => {
  const [drafts, setDrafts] = useState<
    Partial<Record<FeatureLimitKey, number | null>>
  >({});
  const [savingKey, setSavingKey] = useState<FeatureLimitKey | null>(null);
  const [selectedUser, setSelectedUser] = useState<SelectedUser>(null);

  const { data, loading, refetch } = useQuery(GET_FEATURE_LIMITS_GLOBAL, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: storesData, refetch: refetchStores } = useQuery<
    GetStoresByNameAndEmail,
    GetStoresByNameAndEmailVariables
  >(GET_STORES_BY_NAME_AND_EMAIL, {
    variables: { storeRoles: FEATURE_LIMIT_STORE_ROLES },
  });

  const [setGlobalFeatureLimit] = useMutation(SET_GLOBAL_FEATURE_LIMIT);

  const globalLimits: GlobalFeatureLimit[] = data?.featureLimitsGlobal ?? [];
  const limitByKey = new Map(
    globalLimits.map((limit) => [limit.featureKey, limit.monthlyLimit])
  );

  const rows: GlobalRow[] = FEATURE_LIMIT_KEYS.filter(
    (featureKey) => !PER_USER_ONLY_FEATURE_LIMIT_KEYS.includes(featureKey)
  ).map((featureKey) => ({
    featureKey,
    monthlyLimit: limitByKey.get(featureKey) ?? null,
  }));

  const handleSave = async (featureKey: FeatureLimitKey) => {
    const draft = drafts[featureKey] ?? null;
    setSavingKey(featureKey);
    try {
      await setGlobalFeatureLimit({
        variables: {
          input: { featureKey, monthlyLimit: draft },
        },
      });
      successNotification(
        `${FEATURE_LIMIT_LABELS[featureKey]} global limit set to ${formatLimit(
          draft
        )}`
      );
      setDrafts((prev) => ({ ...prev, [featureKey]: undefined }));
      refetch();
    } catch (error) {
      errorNotification((error as Error).message);
    } finally {
      setSavingKey(null);
    }
  };

  const handleOnSearchStores = useMemo(() => {
    const fetchStores = (value: string) => {
      refetchStores({
        searchTerm: value,
        storeRoles: FEATURE_LIMIT_STORE_ROLES,
      });
    };
    return debounce(fetchStores, 400);
  }, [refetchStores]);

  const nameStores = storesData?.nameStores.entities ?? [];
  const emailStores = storesData?.emailStores.entities ?? [];

  const handleSelectUser = (value: string) => {
    const store =
      nameStores.find((item) => item.id === value) ||
      emailStores.find((item) => item.id === value);
    if (store) {
      const fullName = [store.firstName, store.lastName]
        .filter(Boolean)
        .join(' ');
      setSelectedUser({
        userId: store.id,
        name: store.storeDetails?.storeName || fullName || store.id,
      });
    }
  };

  const columns = [
    {
      title: 'Feature',
      key: 'feature',
      width: 280,
      render: (_: unknown, record: GlobalRow) =>
        FEATURE_LIMIT_LABELS[record.featureKey],
    },
    {
      title: 'Current global limit',
      key: 'current',
      width: 160,
      render: (_: unknown, record: GlobalRow) => (
        <Text type={record.monthlyLimit === 0 ? 'danger' : undefined}>
          {formatLimit(record.monthlyLimit)}
        </Text>
      ),
    },
    {
      title: 'New limit (empty = unlimited, 0 = disabled)',
      key: 'edit',
      width: 320,
      render: (_: unknown, record: GlobalRow) => (
        <div className={styles.editCell}>
          <InputNumber
            min={0}
            precision={0}
            placeholder="Unlimited"
            value={drafts[record.featureKey] ?? undefined}
            onChange={(value) =>
              setDrafts((prev) => ({
                ...prev,
                [record.featureKey]: value ?? null,
              }))
            }
          />
          <Button
            type="primary"
            // Nothing to save only when the input is untouched AND the limit is already unlimited.
            disabled={
              drafts[record.featureKey] === undefined &&
              record.monthlyLimit === null
            }
            loading={savingKey === record.featureKey}
            onClick={() => handleSave(record.featureKey)}
          >
            Save
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Title level={4}>Feature Limits — Global Defaults</Title>
        <Text type="secondary">
          Applies to every user without a personal override. Saving with an
          empty input sets the feature back to unlimited.
        </Text>
      </div>

      <Table<GlobalRow>
        rowKey="featureKey"
        columns={columns}
        dataSource={rows}
        loading={loading && !data}
        pagination={false}
        size="middle"
      />

      <div className={styles.userSection}>
        <Title level={4}>Per-User Limits</Title>
        <Text type="secondary">
          Search a user to view usage and set personal overrides. An override
          pins the user and stops following the global default.
        </Text>
        <div className={styles.userSearch}>
          <Select
            placeholder="Search users by name or email"
            showSearch
            filterOption={false}
            onSearch={handleOnSearchStores}
            onSelect={handleSelectUser}
            style={{ width: 400 }}
            value={undefined}
          >
            {nameStores.length ? (
              <OptGroup label="By name">
                {nameStores.map((item) => (
                  <Option key={`name_${item.id}`} value={item.id}>
                    {item.storeDetails?.storeName || item.id}
                  </Option>
                ))}
              </OptGroup>
            ) : null}
            {emailStores.length ? (
              <OptGroup label="By email">
                {emailStores.map((item) => (
                  <Option key={`email_${item.id}`} value={item.id}>
                    {item.email || item.id}
                  </Option>
                ))}
              </OptGroup>
            ) : null}
          </Select>
        </div>
      </div>

      <UserFeatureLimitsDrawer
        userId={selectedUser?.userId ?? null}
        userName={selectedUser?.name ?? ''}
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
};

export default FeatureLimits;
